<script setup lang="ts">
import { computed } from 'vue'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from '@lucide/vue'
import type {
  Notification,
  NotificationSeverity,
} from '@/features/notifications/types/notification'
import { formatRelativeTimestamp } from '@/lib/utils/date'

const props = defineProps<{ notification: Notification }>()

// `!` beats the menu item's focus rule, which recolours every descendant.
const SEVERITY_ICONS = {
  info: { icon: Info, class: 'text-muted-foreground!' },
  success: { icon: CircleCheck, class: 'text-green-600!' },
  warning: { icon: TriangleAlert, class: 'text-amber-500!' },
  error: { icon: CircleAlert, class: 'text-destructive!' },
} satisfies Record<NotificationSeverity, { icon: unknown; class: string }>

const severity = computed(() => SEVERITY_ICONS[props.notification.severity])
const isUnread = computed(() => props.notification.readAt === null)
</script>

<template>
  <component :is="severity.icon" class="mt-0.5 size-4 shrink-0" :class="severity.class" />
  <div class="min-w-0 flex-1 text-left">
    <p class="text-sm leading-snug" :class="isUnread ? 'font-medium' : 'text-muted-foreground'">
      {{ notification.title }}
    </p>
    <p v-if="notification.body" class="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
      {{ notification.body }}
    </p>
    <p class="text-muted-foreground mt-1 text-[11px]">
      {{ formatRelativeTimestamp(notification.createdAt) }}
    </p>
  </div>
  <span v-if="isUnread" class="bg-primary mt-1.5 size-2 shrink-0 rounded-full" />
</template>
